import { useAuth } from "@/context/auth/AuthContext";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import * as AF from "@/components/form-items/AbstractForm";
import useMediaAttachment from "@/hooks/useMediaAttachment";
import { generateImageName } from "@/lib/utils";
import { CroppedImageInput } from "../form-fields/CroppedImageInput";

interface CatalogueRaisonneFormProps {
  formBase: string;
  isMixedCRMode: boolean;
}

export const CatalogueRaisonneGeneralForm = ({
  formBase,
  isMixedCRMode,
}: CatalogueRaisonneFormProps) => {
  const { t } = useTranslation();
  const { instanceData } = useAuth();

  const splitted = useMemo(() => formBase.split("."), [formBase]);

  const imagePosition = useMemo(
    () => `CRGeneral${splitted[splitted.length - 1].toLocaleUpperCase()}`,
    [splitted],
  );

  const { attachment, previewImageUrl, refetch } = useMediaAttachment(
    imagePosition,
    instanceData?.id,
  );

  return (
    <AF.Col>
      <AF.Text
        name={`${formBase}.pageName`}
        label={t("cms.catalogueRaisonne.general.form.pageName")}
      />
      <CroppedImageInput
        docId={instanceData?.id}
        imageURL={previewImageUrl}
        attachment={attachment}
        refetch={refetch}
        imagePosition={imagePosition}
        aspect={1474 / 1160}
        label={t("cms.catalogueRaisonne.general.form.picture")}
        buildImageName={(instanceName) =>
          generateImageName({
            primaryType: "CMSPages",
            instanceName,
            translatedPageName: t("seo.imageName.catalogueRaisonne"),
          })
        }
      />
      <AF.RichText
        name={`${formBase}.content`}
        label={t("cms.catalogueRaisonne.general.form.content")}
      />
      <AF.Text
        name={`${formBase}.artworkCTA`}
        label={
          !isMixedCRMode
            ? t("cms.catalogueRaisonne.general.form.artworkCTA")
            : t("cms.catalogueRaisonne.general.form.artworkCTAMixed")
        }
      />
      {!isMixedCRMode && (
        <AF.Text
          name={`${formBase}.multipleCTA`}
          label={t("cms.catalogueRaisonne.general.form.multipleCTA")}
        />
      )}
      <AF.Switch
        name={`${formBase}.showPublicAuctions`}
        label={t("cms.catalogueRaisonne.general.form.showPublicAuctions")}
      />
    </AF.Col>
  );
};

export const CatalogueRaisonneSpecificForm = ({
  formBase,
  positionBase,
}: CatalogueRaisonneFormProps & { positionBase: string }) => {
  const { t } = useTranslation();
  const { instanceData } = useAuth();

  const splitted = formBase.split(".");

  const imagePosition = useMemo(
    () => `${positionBase}${splitted[splitted.length - 1].toLocaleUpperCase()}`,
    [splitted, positionBase],
  );

  const { attachment, previewImageUrl, refetch } = useMediaAttachment(
    imagePosition,
    instanceData?.id,
  );

  return (
    <AF.Col>
      <AF.Switch
        name={`${formBase}.publishedImage`}
        label={t("cms.catalogueRaisonne.specific.form.publishedImage")}
      />
      <CroppedImageInput
        docId={instanceData?.id}
        imageURL={previewImageUrl}
        attachment={attachment}
        refetch={refetch}
        imagePosition={imagePosition}
        aspect={1474 / 1160}
        label={t("crAccueilImageLabel")}
        buildImageName={(instanceName) =>
          generateImageName({
            primaryType: "CMSPages",
            instanceName,
            translatedPageName: t("seo.imageName.catalogueRaisonne"),
          })
        }
      />
      <AF.Switch
        name={`${formBase}.publishedTitle`}
        label={t("cms.catalogueRaisonne.specific.form.publishedTitle", {
          defaultValue: "Publier le titre",
        })}
      />
      <AF.Text
        name={`${formBase}.pageName`}
        label={t("cms.catalogueRaisonne.specific.form.pageName")}
      />
      <AF.Text
        name={`${formBase}.runningTitle`}
        label={t("cms.catalogueRaisonne.specific.form.runningTitle")}
      />
      <AF.Switch
        name={`${formBase}.publishedText`}
        label={t("cms.catalogueRaisonne.specific.form.publishedText", {
          defaultValue: "Publier le texte de présentation",
        })}
      />
      <AF.RichText
        name={`${formBase}.content`}
        label={t("cms.catalogueRaisonne.specific.form.content")}
      />

      <AF.Switch
        name={`${formBase}.showRegistrationNumber`}
        label={t("cms.catalogueRaisonne.specific.form.showRegistrationNumber")}
      />
    </AF.Col>
  );
};
